import { useEffect, useState } from 'react';

interface UseReferenceOptionsResult<T> {
  options: T[];
  loading: boolean;
  error: unknown;
  refresh: () => Promise<T[]>;
}

export function useReferenceOptions<T>(
  fetcher: () => Promise<T[]>,
  enabled = true,
): UseReferenceOptionsResult<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);

    try {
      const nextOptions = await fetcher();
      setOptions(nextOptions);
      return nextOptions;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!enabled) {
      setOptions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const nextOptions = await fetcher();
        if (isActive) {
          setOptions(nextOptions);
        }
      } catch (nextError) {
        if (isActive) {
          setError(nextError);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [enabled, fetcher]);

  return {
    options,
    loading,
    error,
    refresh,
  };
}
